import React, { useEffect, useState } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Pause, Play, X, Delete, Settings, AudioWaveform, MessageSquare, ArrowLeft, ArrowLeftRight, User } from 'lucide-react';
import { useSIP } from '../../context/SIPContext';
import DeviceSelector from './AudioDeviceSelector';
import CallTransferModal from '../CallTransferModal';

const KEYS = [
    { main: '1', sub: '' }, { main: '2', sub: 'ABC' }, { main: '3', sub: 'DEF' },
    { main: '4', sub: 'GHI' }, { main: '5', sub: 'JKL' }, { main: '6', sub: 'MNO' },
    { main: '7', sub: 'PQRS' }, { main: '8', sub: 'TUV' }, { main: '9', sub: 'WXYZ' },
    { main: '*', sub: '' }, { main: '0', sub: '+' }, { main: '#', sub: '' },
];

const DIALPAD_DOTS = [
    { cx: 4, cy: 4 }, { cx: 12, cy: 4 }, { cx: 20, cy: 4 },
    { cx: 4, cy: 12 }, { cx: 12, cy: 12 }, { cx: 20, cy: 12 },
    { cx: 4, cy: 20 }, { cx: 12, cy: 20 }, { cx: 20, cy: 20 },
    { cx: 12, cy: 28 },
];

const DialpadIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
        {DIALPAD_DOTS.map(({ cx, cy }) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3.4" fill="currentColor" />
        ))}
    </svg>
);

// The single call popup: doubles as the outbound dialer (idle), the in-call
// keypad/controls (calling/active), and the transfer flow — so only one
// popup ever shows.
const ActiveCallPopup = ({ caller, callerNumber, customerNumber, callStatus, isOutgoingCall, onCall, onClose, onHangup, onHold, onUnhold, onMute, onUnmute, isHeld, isMuted, onFeedback }) => {
    const [showDevices, setShowDevices] = useState(false);
    const { sendDTMF, audioDevices, selectedDevices, changeMicrophone, changeSpeaker, isNoiseCancellationEnabled, toggleNoiseCancellation } = useSIP();
    const [number, setNumber] = useState('');
    const [dtmfInput, setDtmfInput] = useState('');
    const [view, setView] = useState('home'); // 'home' | 'keypad' | 'transfer' — only relevant once a call exists

    const isIdle = callStatus === 'idle';
    const isActive = callStatus === 'active';
    // Dialing has no separate "home" screen — the keypad is the whole point.
    const showKeypad = isIdle || view === 'keypad';
    const showTransfer = !isIdle && view === 'transfer';

    // Reset both digit buffers once the call ends, so the next dial starts clean.
    useEffect(() => {
        if (isIdle) {
            setNumber('');
            setDtmfInput('');
        } else {
            setView('home');
        }
    }, [isIdle]);

    const handleKeyClick = (key) => {
        if (isIdle) {
            setNumber(prev => prev + key);
        } else if (isActive) {
            // DTMF only makes sense once the call is actually connected.
            setView('keypad');
            setDtmfInput(prev => prev + key);
            sendDTMF(key);
        }
    };

    const handleDelete = () => {
        if (isIdle) {
            setNumber(prev => prev.slice(0, -1));
        } else if (isActive) {
            setDtmfInput(prev => prev.slice(0, -1));
        }
    };

    const handleCall = () => { if (number && onCall) onCall(number); };

    // Lets the user type/paste a number directly, in addition to tapping keys.
    const handleDisplayChange = (e) => {
        const raw = e.target.value;
        if (isIdle) {
            setNumber(raw.replace(/[^0-9*#+]/g, ''));
        } else {
            const sanitized = raw.replace(/[^0-9*#]/g, '');
            if (sanitized.length > dtmfInput.length && sanitized.startsWith(dtmfInput)) {
                sanitized.slice(dtmfInput.length).split('').forEach(sendDTMF);
            }
            setDtmfInput(sanitized);
        }
    };

    const handleDisplayKeyDown = (e) => {
        if (e.key === 'Enter' && isIdle && number) handleCall();
    };

    // Let the physical keyboard drive the dial pad while this popup is open.
    // Typing into a real <input> (e.g. the transfer view's search/phone fields)
    // is left alone.
    useEffect(() => {
        const handleKeyDown = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;

            if ('0123456789*#'.includes(e.key)) {
                e.preventDefault();
                handleKeyClick(e.key);
            } else if (e.key === 'Backspace') {
                e.preventDefault();
                handleDelete();
            } else if (e.key === 'Enter' && isIdle && number) {
                e.preventDefault();
                handleCall();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    });

    const displayValue = isIdle ? number : dtmfInput;

    const statusLabel = callStatus === 'calling' ? (isOutgoingCall ? 'Calling…' : 'Connecting…') : isHeld ? 'On Hold' : 'In Call';
    const statusDotColor = callStatus === 'calling' ? 'bg-amber-400' : isHeld ? 'bg-teal-400' : 'bg-green-400';

    return (
        <div className="bg-white rounded-2xl shadow-[0_25px_60px_-15px_rgba(15,23,42,0.35)] ring-1 ring-black/5 overflow-hidden w-[420px]">
            {/* Drag handle cue */}
            <div className="flex justify-center pt-2.5 pb-1 cursor-grab">
                <div className="w-9 h-1 rounded-full bg-gray-200" />
            </div>

            {!showTransfer && (
                <div className="flex items-center justify-between px-4 pb-2">
                    {!isIdle && showKeypad ? (
                        <button
                            onClick={() => setView('home')}
                            className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors -ml-1 px-1.5 py-1 rounded-md hover:bg-gray-50"
                            title="Back"
                        >
                            <ArrowLeft size={14} /> Back
                        </button>
                    ) : (
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 tracking-wide uppercase">
                            {isIdle ? (
                                'Dialer'
                            ) : (
                                <>
                                    <span className={`w-1.5 h-1.5 rounded-full ${statusDotColor} ${callStatus !== 'calling' && !isHeld ? 'animate-pulse' : ''}`} />
                                    {statusLabel}
                                </>
                            )}
                        </div>
                    )}
                    {onClose && isIdle ? (
                        <button className="text-gray-300 hover:text-gray-500 hover:bg-gray-50 rounded-full p-1 transition-colors" onClick={onClose} title="Close">
                            <X size={16} />
                        </button>
                    ) : <div />}
                </div>
            )}

            {showTransfer ? (
                <CallTransferModal
                    inline
                    isOpen={showTransfer}
                    onClose={() => setView('home')}
                    callData={{ caller, customerNumber }}
                />
            ) : showKeypad ? (
                <div className="px-4 pb-4">
                    {!isIdle && (
                        <div className="text-center text-sm font-semibold text-gray-800 mb-3 -mt-1">
                            {caller || 'Unknown'}
                        </div>
                    )}

                    {/* Digit display — editable so a number can be typed or pasted in directly */}
                    <div className="bg-gray-50 rounded-xl p-3 mb-4 min-h-[3.25rem] flex items-center justify-center gap-2 ring-1 ring-gray-100">
                        <input
                            type="text"
                            inputMode="tel"
                            autoComplete="off"
                            value={displayValue}
                            onChange={handleDisplayChange}
                            onKeyDown={handleDisplayKeyDown}
                            placeholder="· · ·"
                            className="flex-1 min-w-0 bg-transparent text-2xl font-semibold text-gray-800 text-center tracking-wide focus:outline-none placeholder-gray-300 placeholder:text-xl"
                        />
                        {displayValue && (
                            <button
                                onClick={handleDelete}
                                className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                                title="Delete last digit"
                            >
                                <Delete size={16} />
                            </button>
                        )}
                    </div>

                    {/* Keypad */}
                    <div className="grid grid-cols-3 gap-x-6 gap-y-3 justify-items-center w-fit mx-auto">
                        {KEYS.map((key) => (
                            <button
                                key={key.main}
                                type="button"
                                className="flex flex-col items-center justify-center w-12 h-12 bg-gray-50 hover:bg-gray-100 ring-1 ring-gray-100 rounded-full cursor-pointer transition-all duration-150 active:scale-90 active:bg-gray-200"
                                onClick={() => handleKeyClick(key.main)}
                            >
                                <span className="text-lg font-semibold text-gray-800 leading-none">{key.main}</span>
                                <span className="text-[8px] text-gray-400 uppercase leading-none mt-0.5 tracking-wider">{key.sub}</span>
                            </button>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="px-6 pb-6 pt-1 flex flex-col items-center">
                    <div className="relative mb-3">
                        {callStatus === 'calling' && (
                            <span className="absolute inset-0 rounded-full bg-blue-400/40 animate-ping" />
                        )}
                        <div className="relative w-[4.5rem] h-[4.5rem] rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-semibold shadow-lg ring-4 ring-blue-50">
                            {caller ? caller.charAt(0).toUpperCase() : <User size={30} />}
                        </div>
                    </div>
                    <div className="text-lg font-semibold text-gray-900">{caller || 'Unknown'}</div>
                    {callerNumber && (
                        <div className="text-xs text-gray-400 -mt-0.5">{callerNumber}</div>
                    )}
                    <div className="flex items-center gap-1.5 mt-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDotColor} ${callStatus !== 'calling' && !isHeld ? 'animate-pulse' : ''}`} />
                        <span className="text-sm text-gray-500 font-medium">{statusLabel}</span>
                    </div>

                    <button
                        className="mt-3 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                        onClick={() => setShowDevices(v => !v)}
                    >
                        <Settings size={14} />
                        {showDevices ? 'Hide' : 'Audio'} Settings
                    </button>

                    {/* <span
                        style={{
                            border: '1px solid orange',
                            borderRadius: '10px',
                            padding: '4px 8px',
                            marginTop: '8px',
                            color: 'orange',
                            fontSize: '12px',
                            fontWeight: '600'
                        }}
                    >
                        Refreshing disconnects the current call.
                    </span> */}

                    {showDevices && (
                        <div className="w-full mt-3">
                            <DeviceSelector
                                audioDevices={audioDevices}
                                selectedDevices={selectedDevices}
                                onMicrophoneChange={changeMicrophone}
                                onSpeakerChange={changeSpeaker}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Footer — mute/hold/noise-cancel only make sense once a call exists;
                visible from the start of the call, never hidden behind keypad/transfer */}
            <div className="px-4 pt-3.5 pb-4 border-t border-gray-100 bg-gray-50/60">
                <div className="flex justify-center items-center gap-2.5">
                    <button
                        disabled={isIdle}
                        className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 ${isIdle ? 'opacity-30 pointer-events-none bg-white text-gray-400 ring-1 ring-gray-200' : isNoiseCancellationEnabled ? 'bg-blue-500 text-white shadow-md shadow-blue-200' : 'bg-white hover:bg-gray-100 text-gray-600 ring-1 ring-gray-200'}`}
                        onClick={toggleNoiseCancellation}
                        title={isNoiseCancellationEnabled ? 'Disable Noise Cancellation' : 'Enable Noise Cancellation'}
                    >
                        <AudioWaveform size={18} />
                    </button>
                    <button
                        disabled={isIdle}
                        className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 ${isIdle ? 'opacity-30 pointer-events-none bg-white text-gray-400 ring-1 ring-gray-200' : isMuted ? 'bg-red-500 text-white shadow-md shadow-red-200' : 'bg-white hover:bg-gray-100 text-gray-600 ring-1 ring-gray-200'}`}
                        onClick={isMuted ? onUnmute : onMute}
                        title={isMuted ? 'Unmute' : 'Mute'}
                    >
                        {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                    </button>
                    <button
                        disabled={isIdle}
                        className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 ${isIdle ? 'opacity-30 pointer-events-none bg-white text-gray-400 ring-1 ring-gray-200' : isHeld ? 'bg-gradient-to-br from-[#12B8A6] to-[#17B26A] text-white shadow-md shadow-teal-200' : 'bg-white hover:bg-gray-100 text-gray-600 ring-1 ring-gray-200'}`}
                        onClick={isHeld ? onUnhold : onHold}
                        title={isHeld ? 'Resume' : 'Hold'}
                    >
                        {isHeld ? <Play size={18} /> : <Pause size={18} />}
                    </button>
                    {!isIdle && (
                        <button
                            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 ${view === 'keypad' ? 'bg-blue-500 text-white shadow-md shadow-blue-200' : 'bg-white hover:bg-gray-100 text-gray-600 ring-1 ring-gray-200'}`}
                            onClick={() => setView(view === 'keypad' ? 'home' : 'keypad')}
                            title="Keypad"
                        >
                            <DialpadIcon size={18} />
                        </button>
                    )}
                    {isActive && (
                        <button
                            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 ${view === 'transfer' ? 'bg-blue-500 text-white shadow-md shadow-blue-200' : 'bg-white hover:bg-gray-100 text-gray-600 ring-1 ring-gray-200'}`}
                            onClick={() => setView(view === 'transfer' ? 'home' : 'transfer')}
                            title="Transfer"
                        >
                            <ArrowLeftRight size={18} />
                        </button>
                    )}
                    {isIdle ? (
                        <button
                            disabled={!number}
                            className="w-11 h-11 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center text-white shadow-md shadow-green-200 transition-all duration-200 active:scale-95"
                            onClick={handleCall}
                            title="Call"
                        >
                            <Phone size={18} />
                        </button>
                    ) : (
                        <button
                            className="w-11 h-11 rounded-full bg-gradient-to-br from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 flex items-center justify-center text-white shadow-md shadow-red-200 transition-all duration-200 active:scale-95"
                            onClick={onHangup}
                            title="End Call"
                        >
                            <PhoneOff size={18} />
                        </button>
                    )}

                    <button
                        className="w-11 h-11 rounded-full bg-white hover:bg-gray-100 text-gray-600 ring-1 ring-gray-200 flex items-center justify-center transition-all duration-200"
                        onClick={onFeedback}
                        title="Feedback"
                    >
                        <MessageSquare size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ActiveCallPopup;
